import { Input, InputProps } from '@chakra-ui/react'
import { forwardRef } from 'react'

import { MASKS, TMask } from './masks'

export interface IMaskedInputProps extends Omit<InputProps, 'onChange' | 'value' | 'type' | 'maxLength'> {
  /**
   * Qual máscara. A forma do campo — placeholder, teclado, comprimento — vem junto.
   *
   * `null` é o campo cuja máscara depende de uma escolha que ainda não foi feita: a chave Pix
   * antes de o tipo ser selecionado. Nesse estado o campo é texto puro.
   */
  mask: TMask | null
  value: string | null | undefined
  /** Recebe o valor **já formatado**, que é o que deve ir para o estado da página. */
  onChange: (value: string) => void
}

/**
 * Campo de texto com máscara. O valor que entra e sai é o valor, formatado — a mesma ideia do
 * `<FormSelect>`, onde o que trafega é o valor da opção e não o objeto dela.
 *
 * ```tsx
 * <FormField label="Telefone" error={formErrors.phone} isRequired>
 *   <MaskedInput mask="phone" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
 * </FormField>
 * ```
 *
 * Os 14 campos de telefone do painel estavam em **cinco** implementações: o `react-input-mask` com
 * a máscara fixa `(99) 99999-9999` (4 campos), o `formatPhone` dos helpers (5), uma variante que
 * trunca em 11 dígitos e briga com os ramos de 12–14 do próprio helper
 * (`EntregaDeTrabalhoSubmitPage` e `StudyCreditSubmitPage`, 4 campos), um formatador local que
 * escrevia em `event.target.value` por cima de um input controlado (`VideoQuizForm`, empilhado
 * sobre a máscara da biblioteca) e o `FormLeadsEditModal` — gêmeo do `FormLeadCreateModal` —
 * **sem máscara nenhuma**.
 *
 * As duas primeiras discordam onde mais importa: a máscara fixa da biblioteca só sabe celular de
 * 11 dígitos, então um fixo de 10 sai agrupado errado (`(11) 33334-444`) e um número com DDI não
 * entra. `formatPhone` trata os quatro casos — fixo, celular, DDI de 2 e de 3 dígitos — e é ele
 * que ficou.
 *
 * A formatação é aplicada também ao que chega por prop, então uma página que carrega `'11987654321'`
 * da API não precisa formatar na montagem — o campo mostra `(11) 98765-4321` e o estado converge na
 * primeira edição.
 */
export const MaskedInput = forwardRef<HTMLInputElement, IMaskedInputProps>(function MaskedInput(
  { mask, value, onChange, ...rest },
  ref
) {
  const spec = mask ? MASKS[mask] : null
  const format = (raw: string) => (spec ? spec.format(raw) : raw)

  return (
    <Input
      ref={ref}
      type={spec?.type}
      inputMode={spec?.inputMode}
      autoComplete={spec?.autoComplete}
      placeholder={spec?.placeholder}
      {...rest}
      maxLength={spec?.maxLength}
      value={format(value ?? '')}
      onChange={(event) => onChange(format(event.target.value))}
    />
  )
})
